import { Check, Loader2, X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import type { ColaItem } from '@/modules/bd-unificada/types/cola'

interface ColaTableProps {
  items: ColaItem[]
  soloLectura: boolean
  onAprobar: (id: string) => void
  onRechazar: (id: string) => void
  procesandoId?: string | null
}

/** El contacto entrante viene anidado en `datos_entrantes`, no en el nivel superior del item. */
function datosEntrantes(item: ColaItem): Record<string, unknown> {
  const d = item.datos_entrantes
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : {}
}

function texto(valor: unknown): string {
  return valor == null ? '' : String(valor)
}

/** Nombre principal del registro entrante (con fallback al id si no hay nombre). */
function nombrePrincipal(item: ColaItem): string {
  const d = datosEntrantes(item)
  return texto(d.nombre_completo) || texto(d.nombre) || `Item ${item.id}`
}

/** Línea secundaria: cédula y especialidad. */
function lineaIdentidad(item: ColaItem): string {
  const d = datosEntrantes(item)
  const partes: string[] = []
  if (d.cedula) partes.push(`CI ${texto(d.cedula)}`)
  if (d.especialidad) partes.push(texto(d.especialidad))
  return partes.join(' · ')
}

/** Línea de contacto: teléfono, email y ubicación. */
function lineaContacto(item: ColaItem): string {
  const d = datosEntrantes(item)
  const partes: string[] = []
  if (d.telefono) partes.push(texto(d.telefono))
  const email = d.email_preferido ?? d.email_personal ?? d.email_corporativo
  if (email) partes.push(texto(email))
  const ubicacion = [d.ciudad, d.provincia, d.pais].map(texto).filter(Boolean).join(', ')
  if (ubicacion) partes.push(ubicacion)
  return partes.join(' · ')
}

/** Fecha (solo día) a partir del ISO de created_at. */
function fecha(item: ColaItem): string {
  const raw = texto(item.created_at)
  return raw ? raw.slice(0, 10) : '—'
}

export function ColaTable({
  items,
  soloLectura,
  onAprobar,
  onRechazar,
  procesandoId,
}: ColaTableProps) {
  return (
    <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden">
      <Table>
        <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
          <TableRow>
            <TableHead>Registro entrante</TableHead>
            <TableHead>Motivo</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const identidad = lineaIdentidad(item)
            const contacto = lineaContacto(item)
            return (
            <TableRow key={item.id}>
              <TableCell>
                <div className="font-medium">{nombrePrincipal(item)}</div>
                {identidad && (
                  <div className="text-xs text-muted-foreground">{identidad}</div>
                )}
                {contacto && (
                  <div className="text-xs text-muted-foreground">{contacto}</div>
                )}
              </TableCell>
              <TableCell className="text-sm">{texto(item.razon) || '—'}</TableCell>
              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                {fecha(item)}
              </TableCell>
              <TableCell className="text-right">
                {!soloLectura && (
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAprobar(item.id)}
                      disabled={procesandoId === item.id}
                    >
                      {procesandoId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                      <span className="ml-1">Aprobar</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRechazar(item.id)}
                      disabled={procesandoId === item.id}
                    >
                      <X className="h-4 w-4 text-destructive" />
                      <span className="ml-1">Rechazar</span>
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
